import googleAnalytics from '@analytics/google-analytics'

import type { AnalyticsPlugin } from 'analytics'
import { AnalyticsServiceSettings } from 'models/AnalyticsServiceSettings'
import { flattenObjectForGA4 } from '../helpers/data-layer-converters'

interface GA4PluginConfig {
  isEmbedded: boolean
  campaignId: string
  organizationId: string
}

interface GA4Settings {
  measurement_id: string
}

/**
 * Creates GA4 plugin with proper data flattening
 * !!! --> Only init when not embedded (not in iframe context)
 * (if embedded, GA4 events are handled by cross-frame-analytics)
 */
export const makeGA4AnalyticsPlugin = (
  settings: AnalyticsServiceSettings[],
  config: GA4PluginConfig,
): AnalyticsPlugin | undefined => {
  try {
    if (config.isEmbedded) {
      return undefined
    }

    const ga4Settings = getGA4Settings(settings)

    if (!ga4Settings) {
      return undefined
    }

    const ga4Plugin = googleAnalytics({ measurementIds: [ga4Settings.measurement_id] })

    // Return "enhanced" GA4 plugin (with data flattening)
    return {
      ...ga4Plugin,
      name: 'google-analytics',

      track: (trackPayload: { payload: Record<string, unknown>; [key: string]: unknown }) => {
        const { payload } = trackPayload

        // Flatten properties for GA4 compatibility
        const enhancedPayload = {
          ...payload,
          properties: flattenObjectForGA4(payload.properties as Record<string, unknown>),
        }

        // Call GA4 track method
        ga4Plugin.track({ ...trackPayload, payload: enhancedPayload })
      },
    }
  } catch (error) {
    console.error('Failed to initialize GA4 plugin:', error)
    return undefined
  }
}

/**
 * Extracts and validates GA4 settings from analytics settings array
 */
function getGA4Settings(settings: AnalyticsServiceSettings[]): GA4Settings | null {
  const ga4Setting = settings.find((setting) => setting.service_name === 'google_analytics_4') as
    | AnalyticsServiceSettings<GA4Settings, object>
    | undefined

  if (!ga4Setting?.parameters?.measurement_id) {
    console.warn('GA4 plugin: measurement_id is required but not provided')
    return null
  }

  // Validate measurement_id format
  if (!ga4Setting.parameters.measurement_id.startsWith('G-')) {
    console.warn('GA4 plugin: measurement_id should start with "G-"')
    return null
  }

  return ga4Setting.parameters
}
